"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import { startAuthentication } from "@simplewebauthn/browser"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { KeyRound, Mail } from "lucide-react"

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const router = useRouter()
  const supabase = createClient()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const error = params.get("error")
    const info = params.get("info")
    if (error) setMessage(error)
    else if (info) setMessage(info)
  }, [])

  const handlePasskeySignIn = async () => {
    setLoading(true)
    setMessage("")
    try {
      const optionsRes = await fetch("/api/auth/webauthn/authenticate-options")
      if (!optionsRes.ok) {
        const err = await optionsRes.json()
        throw new Error(err.error || "Nessuna passkey disponibile")
      }
      const options = await optionsRes.json()
      const credential = await startAuthentication({ optionsJSON: options })
      const verifyRes = await fetch("/api/auth/webauthn/authenticate-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credential),
      })
      if (!verifyRes.ok) {
        const err = await verifyRes.json()
        throw new Error(err.error || "Autenticazione fallita")
      }
      const data = await verifyRes.json()
      if (!data.access_token || !data.refresh_token) throw new Error("Tokens mancanti")
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      })
      if (sessionError) throw new Error("Impossibile creare la sessione")
      toast.success("Accesso effettuato con successo!")
      await new Promise((r) => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string }
      if (err.name === "NotAllowedError") {
        setMessage("Autenticazione annullata o timeout. Riprova.")
      } else {
        setMessage(err.message || "Errore durante l'autenticazione con passkey")
        toast.error(err.message || "Errore durante l'autenticazione")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async () => {
    if (!email) { toast.error("Inserisci la tua email"); return }
    setLoading(true)
    setMessage("")
    try {
      const verifyResponse = await fetch("/api/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const verifyData = await verifyResponse.json()
      if (!verifyResponse.ok) {
        if (verifyData.requiresSignup) {
          toast.info("Account non trovato. Ti reindirizziamo alla registrazione...")
          setTimeout(() => router.push(`/auth/signup?email=${encodeURIComponent(email)}`), 1500)
          return
        }
        throw new Error(verifyData.error || "Errore durante la verifica")
      }
      if (verifyData.canSendMagicLink) {
        const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
        if (error) throw error
        setOtpSent(true)
        toast.success("Codice inviato! Controlla la tua email.")
      }
    } catch (error: unknown) {
      const err = error as { message?: string }
      setMessage(err.message || "Errore durante l'invio del codice")
      toast.error(err.message || "Errore durante l'invio del codice")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 8) { toast.error("Inserisci il codice a 8 cifre"); return }
    setLoading(true)
    setMessage("")
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" })
      if (error) throw error
      if (data.user) {
        await supabase.from("users").upsert({
          id: data.user.id,
          email: data.user.email,
          name: data.user.email?.split("@")[0] || "User",
          email_verified: new Date().toISOString(),
        }, { onConflict: "id" })
      }
      toast.success("Accesso effettuato con successo!")
      await new Promise((r) => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (error: unknown) {
      const err = error as { message?: string }
      setMessage(err.message || "Codice non valido o scaduto")
      toast.error(err.message || "Codice non valido o scaduto")
    } finally {
      setLoading(false)
    }
  }

  if (otpSent) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle className="text-xl">Inserisci il codice</CardTitle>
            <CardDescription>
              Ti abbiamo inviato un codice a 8 cifre a{" "}
              <span className="font-semibold text-foreground">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              {message && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
                  {message}
                </div>
              )}
              <Field>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                  placeholder="00000000"
                  className="text-center text-2xl font-bold tracking-widest h-14"
                  autoFocus
                />
              </Field>
              <Field>
                <Button
                  type="button"
                  className="w-full"
                  disabled={loading || otp.length !== 8}
                  onClick={handleVerifyOtp}
                >
                  {loading ? "Verifica in corso..." : "Verifica e Accedi"}
                </Button>
                <FieldDescription className="flex justify-between">
                  <button
                    type="button"
                    className="text-primary underline-offset-4 hover:underline text-sm"
                    onClick={() => { setOtpSent(false); setOtp(""); setMessage("") }}
                  >
                    ← Cambia email
                  </button>
                  <button
                    type="button"
                    className="underline-offset-4 hover:underline text-sm"
                    disabled={loading}
                    onClick={handleSendOtp}
                  >
                    Invia nuovo codice
                  </button>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </CardContent>
        </Card>
        <FieldDescription className="px-6 text-center">
          Non hai ricevuto il codice? Controlla la cartella spam.
        </FieldDescription>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Bentornato</CardTitle>
          <CardDescription>Accedi con passkey o codice email</CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            {message && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
                {message}
              </div>
            )}
            <Field>
              <Button
                type="button"
                className="w-full"
                disabled={loading}
                onClick={handlePasskeySignIn}
              >
                <KeyRound className="h-4 w-4" />
                {loading ? "Caricamento..." : "Accedi con Passkey"}
              </Button>
              <FieldDescription className="text-center">
                💡 Metodo più sicuro e veloce
              </FieldDescription>
            </Field>
            <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
              Oppure con codice email
            </FieldSeparator>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder="tua@email.com"
                required
              />
            </Field>
            <Field>
              <Button
                type="button"
                className="w-full"
                variant="outline"
                disabled={loading || !email}
                onClick={handleSendOtp}
              >
                <Mail className="h-4 w-4" />
                {loading ? "..." : "Invia Codice"}
              </Button>
              <FieldDescription className="text-center">
                Non hai un account?{" "}
                <a href="/auth/signup" className="underline underline-offset-4">
                  Registrati
                </a>
              </FieldDescription>
            </Field>
          </FieldGroup>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        Accedendo accetti i nostri{" "}
        <a href="/terms" className="underline underline-offset-4">Termini di Servizio</a>{" "}
        e{" "}
        <a href="/privacy" className="underline underline-offset-4">Privacy Policy</a>.
      </FieldDescription>
    </div>
  )
}
